import { Currency, isCurrency } from "@redotech/money/currencies";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import {
  getOrderById,
  getReturn,
  getShopifyOrder,
  old_getCurrencyExchangeRates,
} from "@redotech/redo-customer-portal-app/api";
import { AuthContext } from "@redotech/redo-customer-portal-app/contexts/auth";
import {
  SettingsContext,
  StorefrontClientContext,
} from "@redotech/redo-customer-portal-app/contexts/settings";
import { RpcClientContext } from "@redotech/redo-customer-portal-shared/contexts/rpc";
import { ReturnSubmissionResultStatus } from "@redotech/redo-customer-portal-shared/hooks/useReturnConfirmation/use-return-confirmation";
import { getResource } from "@redotech/redo-model/localization/resource";
import { Order } from "@redotech/redo-model/order";
import type { Return } from "@redotech/redo-model/return";
import { ReturnWarning } from "@redotech/redo-model/return";
import { ReturnStatus as ReturnStatusEnum } from "@redotech/redo-model/return-status";
import {
  getReturnTotalsFrontendLogFn,
  ReturnTotals,
  ReturnTotalsCalculator,
} from "@redotech/redo-model/return-totals-calculator";
import { NULL_ORDER_ID } from "@redotech/redo-model/third-party";
import {
  returnToTrackable,
  TrackableType,
} from "@redotech/redo-model/trackable";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import AlertCircle from "@redotech/redo-web/arbiter-icon/alert-circle.svg";
import {
  CURRENCY_FORMAT,
  CurrencyContext,
  CurrencyEnv,
  DEFAULT_CURRENCY,
} from "@redotech/redo-web/currency";
import { getDateString } from "@redotech/redo-web/date-utils";
import { Flex } from "@redotech/redo-web/flex";
import CheckCircle from "@redotech/redo-web/icon-old/check-circle-large.svg";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Text } from "@redotech/redo-web/text";
import { Popover } from "@typeform/embed-react";
import * as classnames from "classnames";
import htmlReactParser from "html-react-parser";
import { memo, useContext, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useDocumentTitle } from "usehooks-ts";
import { FulfillmentDisplay } from "../order-tracking/fulfillment/fulfillment";
import * as returnStyles from "../return-flow/return.module.css";
import { ExchangeHeader } from "../utils";
import { DeadlineCard } from "./deadline-card";
import { NewItemsCard } from "./new-items-card";
import { ReturnItems } from "./return-items";
import * as returnStatus from "./return-status.module.css";
import { ReturnStepsCard } from "./return-steps-card";
import { ShippingCard } from "./shipping-card";
import { SummaryCard } from "./summary-card";
import { WarningsCard } from "./warnings-card";

export type NewOrder = { shopify: { fulfillment_status: string } };

// TODO @JstnMcBrd Should we add a step for flagged? Do we want the customer to know if their return has been flagged by the merchant?
export enum StepType {
  NONE = "none",
  EXCHANGE_ORDER = "exchange_order",
  MANUAL_REVIEW = "manual_review",
  GREEN_RETURN = "green_return",
  IN_TRANSIT = "in_transit",
  DELIVERED = "delivered",
  PROCESSED = "processed",
  WARNING = "warning",
}

export type Step = {
  type: StepType;
  title: string;
  subtitle: string;
  buttons?: JSX.Element;
  complete: boolean;
};

export const openUrl = (url: string) => {
  window.open(url, "_blank");
};

export const ReturnStatus = memo(function ReturnStatus({
  return: return_,
  order = null,
  exchangeOrder = null,
  orderId = null,
}: {
  return?: Return;
  order?: any;
  exchangeOrder?: any;
  orderId?: string | null;
}) {
  const { id } = useParams();
  const [params] = useSearchParams();

  const shopAppLink = params.get("shopAppLink");
  const settings = useContext(SettingsContext)!;
  const auth = useContext(AuthContext);
  const teamId = auth?.teamId;
  const storefrontClient = useContext(StorefrontClientContext);

  const customerPortalClient = useRequiredContext(RpcClientContext);

  const [returnLoad, loadReturn] = useTriggerLoad(async (signal) => {
    let returnData = return_;

    if (!returnData) {
      // @ts-expect-error: strict-null-checks
      const res = await getReturn(id);
      returnData = res.data;
    }
    // @ts-expect-error: strict-null-checks
    const newItems = [...returnData.advancedExchangeItems];
    // @ts-expect-error: strict-null-checks
    returnData.products.forEach((product) => {
      if (product.exchange_for) {
        newItems.push(product.exchange_for);
      }
      if (product.exchangeGroupItem) {
        newItems.push(product.exchangeGroupItem);
      }
    });
    // @ts-expect-error: strict-null-checks
    // Filter our irrelavant warnings
    returnData.warnings = returnData.warnings.filter(
      (warning: ReturnWarning) => {
        // @ts-expect-error: strict-null-checks
        if (warning.type === "label" && returnData.shipment) {
          return false;
          // @ts-expect-error: strict-null-checks
        } else if (warning.type === "pickup" && returnData.shipment?.pickup) {
          return false;
        } else if (
          warning.type === "instant-refund" &&
          // @ts-expect-error: strict-null-checks
          returnData.instantRefund
        ) {
          return false;
          // Claim report warnings are internal and should not be shown to the customer
        } else if (warning.type === "claim-report") {
          return false;
        }

        return true;
      },
    );
    return { returnData, newItems };
  });

  const [isWorkflowFinished, setIsWorkflowFinished] = useState(false);

  // @ts-expect-error: strict-null-checks
  const isUsingNewSubmitReturn = settings.returns.useNewSubmitReturn === true;

  useEffect(() => {
    if (returnLoad.error || returnLoad.pending) {
      return () => {};
    }

    if (!returnLoad.value) {
      loadReturn();
      return () => {};
    }

    if (!isUsingNewSubmitReturn) {
      return () => {};
    }

    if (isWorkflowFinished) {
      return () => {};
    }

    //We need to ping workflow on an interval to check if it's finished then call loadReturn

    async function checkWorkflow() {
      const workflowStatus =
        await customerPortalClient.getSubmissionWorkflowStatus({
          // @ts-expect-error: strict-null-checks
          returnId: id,
        });

      if (workflowStatus.noWorkflow === true) {
        setIsWorkflowFinished(true);
        return;
      }

      if (workflowStatus.finished) {
        setIsWorkflowFinished(true);
        loadReturn();
      }
    }

    const interval = setInterval(checkWorkflow, 3000);

    return () => clearInterval(interval);
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnLoad, customerPortalClient, id, isWorkflowFinished, loadReturn]);

  const { returnData, newItems = [] } = returnLoad.value || {};

  const copyResource = getResource({
    variant: returnData?.type || "return",
    overrides: settings.resourceOverride,
  });

  type OrderInfo = {
    orders: Order[];
    orderDate: string;
    returnTotals: ReturnTotals;

    orderCurrencyEnv: CurrencyEnv;
  };

  const ordersLoad = useLoad(
    async (signal): Promise<OrderInfo> => {
      if (!returnData) {
        // @ts-expect-error: strict-null-checks
        return null;
      }
      const orders = [];

      if (order) {
        orders.push(order);
      } else {
        for (const order of returnData.orders) {
          const res = await getOrderById(order.order);

          orders.push(res.data.order);
        }
      }

      const calculator = new ReturnTotalsCalculator({
        return_: returnData,

        order: orders[0],
        orders,
        team: { settings },
        originOrders: returnData.originOrders,
        logFn: getReturnTotalsFrontendLogFn(),
      });

      const rawTotals = calculator.getTotalsForAllProducts();
      // TODO: probably need a better way to compute whether it was an instant refund
      const isInstantRefund =
        returnData.totals.refund ===
        rawTotals.totalRefundWithoutShipping * 0.97;
      const returnTotals = calculator.getTotalsForAllProducts(isInstantRefund);

      // TODO: remove duplicated code. This same currency logic is done in to build the original CurrencyContext.
      // TODO: note we also fetch exchange rates here
      const shopCurrencyStr: string | undefined = order?.shopify?.currency;
      const orderCurrencyStr: string | undefined =
        order?.shopify?.presentment_currency;

      const shopCurrency =
        shopCurrencyStr && isCurrency(shopCurrencyStr)
          ? shopCurrencyStr
          : DEFAULT_CURRENCY;
      const orderCurrency =
        orderCurrencyStr && isCurrency(orderCurrencyStr)
          ? orderCurrencyStr
          : DEFAULT_CURRENCY;

      let usdRatesPromise;

      if (orderCurrency !== Currency.USD) {
        usdRatesPromise = old_getCurrencyExchangeRates(Currency.USD);
      }
      let formatCurrency: (amount: number) => string;
      if (orderCurrency !== shopCurrency) {
        try {
          const exchangeRates = (
            await old_getCurrencyExchangeRates(shopCurrency)
          ).data;
          // @ts-expect-error: strict-null-checks
          formatCurrency = exchangeRates
            ? (amount: number) => {
                return CURRENCY_FORMAT(orderCurrency).format(
                  amount * exchangeRates[orderCurrency],
                );
              }
            : undefined;
        } catch (e) {
          console.error(e);
        }
      }
      // @ts-expect-error: strict-null-checks
      if (formatCurrency === undefined) {
        formatCurrency = (amount: number) =>
          CURRENCY_FORMAT(shopCurrency).format(amount);
      }

      const usdRates = usdRatesPromise
        ? (await usdRatesPromise).data
        : undefined;

      const formatCurrencyFromUSD = usdRates
        ? (amount: number) => {
            return CURRENCY_FORMAT(orderCurrency).format(
              amount * usdRates[orderCurrency],
            );
          }
        : (amount: number) => CURRENCY_FORMAT(Currency.USD).format(amount);

      return {
        orders,
        returnTotals,
        orderDate: getDateString(new Date(orders[0].createdAt)),
        orderCurrencyEnv: {
          currency: orderCurrency,
          formatCurrency,
          formatCurrencyFromUSD,
          setCurrency: (_currency) => {},
        } as CurrencyEnv,
      };
    },
    [returnData],
  );

  const newOrderLoad = useLoad(async () => {
    let newOrder: NewOrder;
    let newOrderNumber = "";
    if (returnData?.exchangeOrder.length) {
      const res = await getShopifyOrder(returnData.exchangeOrder[0].id);
      newOrder = { shopify: res.data.order };
      newOrderNumber = res.data.order.name;
    } else {
      newOrder = { shopify: { fulfillment_status: "unfulfilled" } };
    }

    return { newOrder, newOrderNumber };
  }, [returnData]);

  const rejected = returnData?.status === ReturnStatusEnum.REJECTED;

  const returnSubmissionResultStatus = useMemo(() => {
    if (!returnData) {
      return undefined;
    } else if (rejected) {
      return ReturnSubmissionResultStatus.REJECTED_RETURN;
    } else if (returnData.warnings.length > 0) {
      return ReturnSubmissionResultStatus.CREATED_RETURN_WITH_ERRORS;
    } else {
      return ReturnSubmissionResultStatus.CREATED_RETURN;
    }
  }, [rejected, returnData]);

  const returnStatusHeaderText = useMemo(() => {
    switch (returnSubmissionResultStatus) {
      case ReturnSubmissionResultStatus.CREATED_RETURN:
        return copyResource.CONFIRMATION_HEADER_TEXT_SUBMIT;
      case ReturnSubmissionResultStatus.CREATED_RETURN_WITH_ERRORS:
        return copyResource.CONFIRMATION_HEADER_TEXT_SUBMIT_WITH_ERRORS;
      case ReturnSubmissionResultStatus.REJECTED_RETURN:
        return copyResource.CONFIRMATION_HEADER_TEXT_REJECT;
      case undefined:
        return "";
    }
  }, [returnSubmissionResultStatus, copyResource]);

  const returnStatusHeaderIcon = useMemo(() => {
    switch (returnSubmissionResultStatus) {
      case ReturnSubmissionResultStatus.CREATED_RETURN:
        return (
          <CheckCircle
            className={classnames(
              returnStatus.returnStatusHeaderIcon,
              returnStatus.checkCircle,
            )}
          />
        );
      case ReturnSubmissionResultStatus.CREATED_RETURN_WITH_ERRORS:
        return (
          <AlertCircle
            className={classnames(
              returnStatus.returnStatusHeaderIcon,
              returnStatus.alertCircle,
            )}
          />
        );
      case ReturnSubmissionResultStatus.REJECTED_RETURN:
      case undefined:
        return null;
    }
  }, [returnSubmissionResultStatus]);

  useDocumentTitle(copyResource.DOCUMENT_TITLE);

  const { newOrder } = newOrderLoad.value || {};

  if (returnLoad.pending || ordersLoad.pending) {
    return <LoadingRedoAnimation />;
  }
  if (returnLoad.error || ordersLoad.error || newOrderLoad.error) {
    return (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <div className={returnStatus.title}>Error loading return status</div>
      </div>
    );
  }

  if (!returnData || !ordersLoad.value) {
    return null;
  }

  const trackable = returnToTrackable(
    returnData,

    ordersLoad.value.orders[0] as Order,
  );

  if (
    settings.returnTracking?.enabled &&
    ordersLoad.value &&
    returnData.status !== ReturnStatusEnum.NEEDS_REVIEW &&
    returnData.status !== ReturnStatusEnum.OPEN
  ) {
    return (
      <FulfillmentDisplay
        editMode={false}
        highlightedElement={null}
        onInsertAfter={() => {}}
        onSelect={() => {}}
        setAdSections={() => {}}
        setModified={() => {}}
        settings={settings}
        storefrontClient={storefrontClient}
        trackable={trackable}
      />
    );
  }

  const isExchange =
    returnData.advancedExchangeItems?.length ||
    returnData.products.some(
      (product) => product.exchange_for || product.exchangeGroupItem,
    );
  const showShippingInformation =
    !!returnData.shipping_address &&
    isExchange &&
    !returnData.exchangeOrder.length &&
    !returnData.draftOrderId;

  const deadlineDays = Math.max(
    0,
    Math.round(
      Temporal.Now.instant()
        // FIXME Our types are all wonky - expirationDate is really a string, not a Date object

        .until(
          new Date(
            returnData.expirationDate as unknown as string,
          ).toTemporalInstant(),
        )
        .total("days"),
    ),
  );

  const needToPayDraftOrder =
    returnData.type === "return" &&
    returnData.draftOrderURL &&
    !returnData.exchangeOrder.length;

  const showSummaryCard = !rejected && !settings.hideSummaryCard;
  const info = ordersLoad.value;

  const orderDisplayName =
    !info.orders[0]?.shopify.name ||
    info.orders[0].shopify.name === NULL_ORDER_ID
      ? copyResource.ORDERLESS_DISPLAY_NAME
      : info.orders.map((order) => order.shopify.name).join(" & ");

  return (
    <CurrencyContext.Provider value={info.orderCurrencyEnv}>
      <div className={returnStatus.page}>
        {needToPayDraftOrder ? (
          <ExchangeHeader returnData={returnData} />
        ) : (
          <div className={returnStatus.title}>
            {returnStatusHeaderIcon}
            {returnStatusHeaderText}
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div className={returnStatus.header}>
            <div className={returnStyles.flex}>
              <div className={returnStyles.grayTextOnGrayBg}>Order:&nbsp;</div>
              <div className={returnStyles.orderNumber}>
                {/*We used to handle returns across multiple orders but now we are just doing one so this should be ok to get first in array*/}

                {orderDisplayName}
              </div>
            </div>
            {info.orders.length === 1 && (
              <div className={returnStyles.grayTextOnGrayBg}>
                Date: {info.orderDate}
              </div>
            )}
          </div>
        </div>
        {shopAppLink ? (
          <Flex justify="center" pt="xl">
            <RedoButton
              hierarchy="secondary"
              onClick={() => window.open(shopAppLink)}
              text="Click here to return to Shop App"
            />
          </Flex>
        ) : null}

        <div className={returnStatus.mainContent}>
          {rejected ? (
            <div className={returnStatus.text}>
              {htmlReactParser(returnData.products[0]?.rejectMessage ?? "")}
            </div>
          ) : (
            settings.theme.custom_confirmation_text && (
              <div className={returnStatus.text}>
                {htmlReactParser(settings.theme.custom_confirmation_text)}
              </div>
            )
          )}
          {!isWorkflowFinished && isUsingNewSubmitReturn && (
            <Flex dir="row" justify="center">
              <Text>Creating return please wait...</Text>
            </Flex>
          )}
          {!rejected && (
            <>
              {returnData.warnings?.length > 0 && (
                <WarningsCard warnings={returnData.warnings} />
              )}

              {trackable.type === TrackableType.RETURN && (
                <ReturnStepsCard settings={settings} trackable={trackable} />
              )}

              {deadlineDays <= 10 &&
                returnData.products.some(
                  (product) => !product.green_return,
                ) && <DeadlineCard deadlineDays={deadlineDays} />}
            </>
          )}
          <ReturnItems returnData={returnData} />
          {newItems.length > 0 && !rejected && newOrder && (
            <NewItemsCard
              newItems={newItems}
              newOrder={newOrder}
              returnType={returnData.type}
            />
          )}
          {showSummaryCard && (
            <>
              <SummaryCard
                newItems={newItems}
                returnData={returnData}
                returnTotals={info.returnTotals}
              />
              {showShippingInformation && (
                <ShippingCard returnData={returnData} />
              )}
            </>
          )}
        </div>
      </div>
      {!settings.theme?.hideRateYourExperienceWidget && teamId && (
        <Popover
          buttonColor="var(--redo-primary-color)"
          hidden={{
            team_id: teamId,
            // @ts-expect-error: strict-null-checks
            order_id: orderId ?? undefined,
            return_id: returnData._id,
          }}
          id="ervbmPsn"
          keepSession
          tooltip="Please tell us about your return experience"
        />
      )}
    </CurrencyContext.Provider>
  );
});
